import { useCallback } from 'react';
import { useAnalysisContext } from '../context/AnalysisContext';

/**
 * Hook for exporting analysis results in various formats
 */
export const useResultsExport = () => {
  const context = useAnalysisContext();

  // ========================================
  // CSV EXPORT
  // ========================================

  const exportToCSV = useCallback(
    (data: any[], filename: string = 'analysis_results.csv') => {
      if (!data || data.length === 0) {
        console.error('No data to export');
        return;
      }

      // Get all unique keys from all objects
      const keys = Array.from(
        new Set(data.flatMap((item) => Object.keys(item)))
      );

      // Create CSV header
      const header = keys.join(',');

      // Create CSV rows
      const rows = data.map((item) => {
        return keys
          .map((key) => {
            const value = item[key];
            // Handle values that contain commas or quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value ?? '';
          })
          .join(',');
      });

      // Combine header and rows
      const csv = [header, ...rows].join('\n');

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    []
  );

  // ========================================
  // JSON EXPORT
  // ========================================

  const exportToJSON = useCallback(
    (data: any, filename: string = 'analysis_results.json') => {
      if (!data) {
        console.error('No data to export');
        return;
      }

      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    []
  );

  // ========================================
  // COMPLETE ANALYSIS EXPORT
  // ========================================

  const exportCompleteAnalysis = useCallback(() => {
    const exportData = {
      metadata: {
        exportDate: new Date().toISOString(),
        disease: context.selectedDisease,
        cellTypes: context.selectedCellTypes,
        analysisType: context.selectedAnalysisType,
        parameters: context.parameters[context.selectedAnalysisType],
        preset: context.preset,
      },
      results: context.analysisResult,
    };

    const filename = `${context.selectedDisease?.replace(/\s+/g, '_')}_${context.selectedAnalysisType}_analysis.json`;
    exportToJSON(exportData, filename);
  }, [
    context.selectedDisease,
    context.selectedCellTypes,
    context.selectedAnalysisType,
    context.parameters,
    context.preset,
    context.analysisResult,
    exportToJSON,
  ]);

  // ========================================
  // SESSION STATE EXPORT
  // ========================================

  const exportSession = useCallback(() => {
    const sessionData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      workflow: {
        currentStep: context.currentStep,
        completedSteps: context.completedSteps,
        selectedDisease: context.selectedDisease,
        diseaseMetadata: context.diseaseMetadata,
        selectedCellTypes: context.selectedCellTypes,
        selectedAnalysisType: context.selectedAnalysisType,
        parameters: context.parameters,
        preset: context.preset,
      },
      results: context.analysisResult,
    };

    const filename = `rnaverse_session_${new Date().toISOString().split('T')[0]}.json`;
    exportToJSON(sessionData, filename);
  }, [context]);

  // ========================================
  // IMAGE EXPORT (for visualizations)
  // ========================================

  const exportImage = useCallback(
    async (
      elementId: string,
      filename: string = 'visualization.png',
      format: 'png' | 'svg' = 'png'
    ) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`Element with id "${elementId}" not found`);
        return;
      }

      try {
        // For SVG elements
        if (format === 'svg') {
          const svgElement = element.querySelector('svg');
          if (!svgElement) {
            console.error('No SVG element found');
            return;
          }

          const serializer = new XMLSerializer();
          const svgString = serializer.serializeToString(svgElement);
          const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.click();
          URL.revokeObjectURL(url);
        } else {
          // For PNG using html2canvas (would need to install library)
          console.warn('PNG export requires html2canvas library. Falling back to SVG.');
          exportImage(elementId, filename.replace('.png', '.svg'), 'svg');
        }
      } catch (error) {
        console.error('Error exporting image:', error);
      }
    },
    []
  );

  // ========================================
  // GENERATE SHAREABLE URL
  // ========================================

  const generateShareURL = useCallback((): string => {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams();

    params.set('step', context.currentStep.toString());

    if (context.selectedDisease) {
      params.set('disease', context.selectedDisease);
    }

    const allCells = [
      ...context.selectedCellTypes.disease,
      ...context.selectedCellTypes.normal,
    ];
    if (allCells.length > 0) {
      params.set('cells', allCells.join(','));
    }

    if (context.selectedAnalysisType !== 'correlation') {
      params.set('analysis', context.selectedAnalysisType);
    }

    if (context.preset === 'custom') {
      try {
        const paramJson = JSON.stringify(context.parameters);
        params.set('params', btoa(paramJson));
      } catch (e) {
        console.error('Failed to encode parameters:', e);
      }
    } else if (context.preset !== 'standard') {
      params.set('preset', context.preset);
    }

    return `${baseUrl}?${params.toString()}`;
  }, [context]);

  // ========================================
  // COPY SHARE URL TO CLIPBOARD
  // ========================================

  const copyShareURL = useCallback(async (): Promise<boolean> => {
    try {
      const url = generateShareURL();
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      console.error('Failed to copy URL:', error);
      return false;
    }
  }, [generateShareURL]);

  // ========================================
  // TABLE DATA PREPARATION
  // ========================================

  const prepareTableData = useCallback((results: any): any[] => {
    if (!results) return [];

    // Handle different result formats
    if (Array.isArray(results)) {
      return results;
    }

    // For correlation analysis results
    if (results.lncrna_results) {
      return results.lncrna_results;
    }

    // For differential expression results
    if (results.lncrna_expression) {
      return results.lncrna_expression;
    }

    // Generic fallback
    return [];
  }, []);

  // ========================================
  // EXPORT CURRENT RESULTS TABLE
  // ========================================

  const exportResultsTable = useCallback(() => {
    const tableData = prepareTableData(context.analysisResult);
    if (tableData.length === 0) {
      console.error('No table data to export');
      return;
    }

    const filename = `${context.selectedDisease?.replace(/\s+/g, '_')}_${context.selectedAnalysisType}_results.csv`;
    exportToCSV(tableData, filename);
  }, [context.analysisResult, context.selectedDisease, context.selectedAnalysisType, prepareTableData, exportToCSV]);

  // ========================================
  // RETURN VALUE
  // ========================================

  return {
    // Basic exports
    exportToCSV,
    exportToJSON,
    exportImage,

    // Specific exports
    exportCompleteAnalysis,
    exportSession,
    exportResultsTable,

    // Sharing
    generateShareURL,
    copyShareURL,

    // Utilities
    prepareTableData,

    // State
    hasResults: !!context.analysisResult,
    canExport: !!context.analysisResult && !context.isAnalyzing,
  };
};

export default useResultsExport;
